// src/shared/streaming/SignalRegistry.ts
// ────────────────────────────────────────────────────────────────────────────
// Signal Family Registry — FE TypeScript Mirror
//
// Single source of truth for all signal family definitions on the FE side.
// Must stay in sync with: server/bff/lib/signal-registry.mjs
//
// All downstream types, maps, and lookups are derived from SIGNAL_FAMILIES.
// ────────────────────────────────────────────────────────────────────────────

// ── Types ────────────────────────────────────────────────────────────────

export interface SignalDef {
  readonly key:    string;
  readonly wireId: number;
  readonly idType: number;
  readonly unit:   string;
  readonly label:  string;
  readonly color:  string;
}

export interface SignalFamily {
  readonly family:              string;
  readonly scopeNs:             string;
  readonly encoding:            'binary' | 'json';
  readonly downsampleThreshold: number;
  readonly perScopeLimit:       number;
  readonly signals:             readonly SignalDef[];
}

// ── Registry ─────────────────────────────────────────────────────────────

export const SIGNAL_FAMILIES: readonly SignalFamily[] = [
  // ── IoT (ESP32 sensors) ─────────────────────────────────────────
  {
    family: 'iot', scopeNs: 'iot', encoding: 'binary',
    downsampleThreshold: 800, perScopeLimit: 2000,
    signals: [
      { key: 'temp',     wireId: 0x01, idType: 1,  unit: '°C',  label: 'Temperature',   color: '#34d399' },
      { key: 'hum',      wireId: 0x02, idType: 2,  unit: '%',   label: 'Humidity',      color: '#60a5fa' },
      { key: 'rssi',     wireId: 0x04, idType: 4,  unit: 'dBm', label: 'RSSI',          color: '#f97316' },
      { key: 'thermo_c', wireId: 0x05, idType: 5,  unit: '°C',  label: 'Thermocouple',  color: '#ef4444' },
    ],
  },
  // ── PACDaemon (system metrics) ──────────────────────────────────
  {
    family: 'pacdaemon', scopeNs: 'pacdaemon', encoding: 'binary',
    downsampleThreshold: 800, perScopeLimit: 2000,
    signals: [
      { key: 'cpu',            wireId: 0x10, idType: 16, unit: '%',     label: 'CPU',             color: '#f87171' },
      { key: 'memory',         wireId: 0x11, idType: 17, unit: '%',     label: 'Memory',          color: '#a78bfa' },
      { key: 'disk',           wireId: 0x12, idType: 18, unit: '%',     label: 'Disk',            color: '#fbbf24' },
      { key: 'disk_c_used_gb', wireId: 0x13, idType: 19, unit: 'GB',    label: 'Disk C Used',     color: '#fb923c' },
      { key: 'disk_e_used_gb', wireId: 0x14, idType: 20, unit: 'GB',    label: 'Disk E Used',     color: '#e879f9' },
      { key: 'network',        wireId: 0x15, idType: 21, unit: 'Mbps',  label: 'Network',         color: '#38bdf8' },
      { key: 'power',          wireId: 0x16, idType: 22, unit: 'W',     label: 'Power',           color: '#4ade80' },
      { key: 'process',        wireId: 0x17, idType: 23, unit: 'count', label: 'Processes',       color: '#94a3b8' },
      { key: 'temperature',    wireId: 0x18, idType: 24, unit: '°C',    label: 'CPU Temperature', color: '#ef4444' },
    ],
  },
  // ── Visitor (web analytics) ─────────────────────────────────────
  {
    family: 'visitor', scopeNs: 'visitor', encoding: 'binary',
    downsampleThreshold: 800, perScopeLimit: 2000,
    signals: [
      { key: 'sessions',  wireId: 0x20, idType: 200, unit: 'count', label: 'Sessions',   color: '#22d3ee' },
      { key: 'pageviews', wireId: 0x21, idType: 201, unit: 'count', label: 'Page Views', color: '#818cf8' },
    ],
  },
  // ── Forensic (ingestion diagnostic) ─────────────────────────────
  {
    family: 'forensic', scopeNs: 'forensic', encoding: 'binary',
    downsampleThreshold: 800, perScopeLimit: 2000,
    signals: [
      { key: 'ingest_count', wireId: 0x30, idType: 10, unit: 'count', label: 'Ingest Count', color: '#d946ef' },
    ],
  },
  // ── TableOps (operations) ───────────────────────────────────────
  {
    family: 'tableops', scopeNs: 'tableops', encoding: 'binary',
    downsampleThreshold: 800, perScopeLimit: 2000,
    signals: [
      { key: 'orders',   wireId: 0x40, idType: 300, unit: 'count', label: 'Orders',   color: '#2dd4bf' },
      { key: 'payments', wireId: 0x41, idType: 301, unit: 'count', label: 'Payments', color: '#86efac' },
      { key: 'shifts',   wireId: 0x42, idType: 302, unit: 'count', label: 'Shifts',   color: '#fde047' },
    ],
  },
  // ── RetailOps (retail transactions) ─────────────────────────────
  {
    family: 'retailops', scopeNs: 'retailops', encoding: 'binary',
    downsampleThreshold: 800, perScopeLimit: 2000,
    signals: [
      { key: 'transactions', wireId: 0x50, idType: 400, unit: 'count', label: 'Transactions', color: '#c084fc' },
    ],
  },
] as const;

// ── Derived constants (computed once at module load) ──────────────────────

const _allSignals: readonly SignalDef[] = SIGNAL_FAMILIES.flatMap(f => f.signals);

/**
 * METRIC_ID — wire ID map matching the legacy shape.
 * Drop-in replacement for the old hard-coded const.
 */
export const METRIC_ID: Record<string, number> = Object.fromEntries(
  _allSignals.map(s => [s.key, s.wireId]),
);

/** MetricKey — union of all known signal keys. */
export type MetricKey = (typeof SIGNAL_FAMILIES)[number]['signals'][number]['key'];

/** Reverse map: wire id → metric key. */
export const ID_TO_KEY: Record<number, string> = Object.fromEntries(
  _allSignals.map(s => [s.wireId, s.key]),
);

/** Metadata per signal key: unit, label, color. */
export const METRIC_META: Record<string, { unit: string; label: string; color: string }> =
  Object.fromEntries(
    _allSignals.map(s => [s.key, { unit: s.unit, label: s.label, color: s.color }]),
  );

// ── Lookup helpers ───────────────────────────────────────────────────────

const _byNs     = new Map(SIGNAL_FAMILIES.map(f => [f.scopeNs, f]));
const _byWireId = new Map(_allSignals.map(s => [s.wireId, s]));

export function getFamilyByNs(scopeNs: string): SignalFamily | null {
  return _byNs.get(scopeNs) ?? null;
}

export function getSignalByWireId(wireId: number): SignalDef | null {
  return _byWireId.get(wireId) ?? null;
}

export function getMetricMeta(key: string): { unit: string; label: string; color: string } | null {
  return METRIC_META[key] ?? null;
}

/** All signal keys — flat array, useful for iterating all metrics. */
export const ALL_SIGNAL_KEYS: string[] = _allSignals.map(s => s.key);

// ── Namespace-scoped key sets (precomputed, O(1) lookup) ─────────────────

const _keysByNs = new Map<string, string[]>();
for (const f of SIGNAL_FAMILIES) {
  _keysByNs.set(f.scopeNs, f.signals.map(s => s.key));
}

/**
 * Return metric keys for a specific scope namespace.
 * Falls back to ALL keys when the namespace is unknown.
 * Pre-computed — no allocation on the hot path.
 */
export function getKeysForNs(scopeNs: string | undefined): string[] {
  if (!scopeNs) return ALL_SIGNAL_KEYS;
  return _keysByNs.get(scopeNs) ?? ALL_SIGNAL_KEYS;
}
